<template>
  <AppCartSidebarRow
    v-if="bundleName"
    :title="bundleName"
    :image-url="cartBundlePreviewImgUrl"
    :description-html="descriptionHtml"
    :description="descriptionText"
    :selections="selections"
    :is-free="isReward"
    :is-arriving="isArriving"
  >
    <template #actions>
      <AppCartSidebarRemoveButton
        v-if="showRemoveButton"
        :title="bundleName"
        :disabled="cartStore.mutationsBlocked"
        @remove="handleRemovePackageBtnClick"
      />
    </template>
    <template #price>
      <CartBundleItemPrice
        :cart-bundle="cartBundle"
        :variant="priceVariant"
      />
    </template>
  </AppCartSidebarRow>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'
import { useToast } from 'vue-toastification'

import { useCartStore } from '@/stores/cart'
import { useCatalogStore } from '@/stores/catalog'
import { useCommerceStore } from '@/stores/commerce'
import { mapState, mapStores } from 'pinia'

import { cloneDeep } from 'lodash-es'

import { renderRichTextToHtml } from '@/composables/contentful'
import { useGtmTracking } from '@/composables/gtmTracking'
import { cartChangeErrorMessage } from '@/lib/cartErrors'
import { rewardCartItemDescriptionHtml } from '@/lib/rewardCartItemContent'

import CartBundleItemPrice from '@/components/Cart/CartBundleItem/CartBundleItemPrice.vue'
import { CartBundleItemPriceVariant } from '@/components/Cart/CartBundleItem/CartBundleItemPriceVariant'

import { CartBundleSelectionItemData } from '@/types/CartBundleSelection'
import { GoogleAnalyticsCartItem } from '@/types/GoogleAnalyticsCartItem'

import { CartBundleStorefrontDto } from '@VelocitySquared/sunny16-commerce-dataobjects/storefront'

import AppCartSidebarRemoveButton from './AppCartSidebarRemoveButton.vue'
import AppCartSidebarRow from './AppCartSidebarRow.vue'
import { cartSidebarContent } from '@/content/cartSidebar'
import { feedbackContent } from '@/content/feedback'

export default defineComponent({
  name: 'AppCartSidebarBundleItem',
  components: { AppCartSidebarRow, AppCartSidebarRemoveButton, CartBundleItemPrice },
  setup() {
    return {
      ...useGtmTracking(),
      toast: useToast(),
      // Named rather than hardcoding the enum's string value in the template.
      priceVariant: CartBundleItemPriceVariant.Inline
    }
  },
  props: {
    cartBundle: {
      type: Object as PropType<CartBundleStorefrontDto>,
      required: true
    },
    /**
     * Plays the row's arrival flash — set by the unlock sequence's reveal beat (A2b).
     *
     * A prop rather than a store read for the same reason the sequence's other visual state is: the
     * beat belongs to one run, and `AppCartSidebar` is what knows which run is in flight.
     */
    isArriving: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapStores(useCartStore),
    ...mapState(useCatalogStore, ['catalogBundleContent', 'catalogData', 'promotionContent']),
    ...mapState(useCartStore, ['adHocCartBundlePreviewImageUrl']),
    ...mapState(useCommerceStore, [
      'cartBundleExtendedInfo',
      'cartBundleIsRemovable',
      'cartBundleSelections'
    ]),
    bundleContent() {
      return this.catalogBundleContent(this.cartBundle.bundleId)
    },
    bundleName(): string | null {
      if (this.bundleContent) {
        // Use the name from the bundle content if available
        return this.bundleContent.name
      } else {
        // Fallback to the internal name from the catalog data
        const catalogBundle = this.catalogData?.catalogBundles?.find(
          (b) => b.catalogBundleId === this.cartBundle.catalogBundleId
        )

        if (catalogBundle) {
          return catalogBundle.bundle!.nameInternal
        }
      }

      // If we reach here, we couldn't find a name, so log an error
      window.newrelic.noticeError(
        new Error('Could not find a bundleName for a cart bundle item in AppCartSidebarBundleItem'),
        {
          bundleId: this.cartBundle.bundleId
        }
      )

      return null
    },
    // The board draws a description on every row ("Includes (1) 11x14, (4) 8x10, …"), which is the
    // authored `cartDescription` rich text — the same source the main cart uses. `extendedInfo` is
    // kept alongside it because it only ever resolves for `UC_Background` rows and carries different
    // information. `renderRichTextToHtml` sanitizes via DOMPurify, so `v-html` is safe here.
    cartDescriptionHtml(): string | null {
      const cartDescription = this.bundleContent?.cartDescription

      return cartDescription ? renderRichTextToHtml(cartDescription) : null
    },
    // Rich-text description shown under the title.
    //
    // For a reward row the granting promotion gets first say — `rewardCartItemDescription` is the
    // authored replacement for what used to be a hard-coded "Reward Unlocked!" — then the bundle's own
    // `cartDescription`, then the constant via `descriptionText` below. Falling through to the package
    // contents is deliberate (2026-08-05): more informative than "Reward Unlocked!", and the row still
    // reads as a reward from the FREE pill and the `$0.00`.
    descriptionHtml(): string | null {
      if (!this.isReward) {
        return this.cartDescriptionHtml
      }

      return (
        rewardCartItemDescriptionHtml(this.promotionContent(this.cartBundle.sourcePromotionId)) ??
        this.cartDescriptionHtml
      )
    },
    // Plain-text line. Reward rows fall back to "Reward Unlocked!" only when neither the promotion nor
    // the bundle authored anything; ordinary rows keep `extendedInfo`, which resolves for
    // `UC_Background` bundles alone and carries different information from the description.
    descriptionText(): string | null {
      if (!this.isReward) {
        return this.extendedInfo
      }

      return this.descriptionHtml ? null : cartSidebarContent.rewardUnlockedDescription
    },
    // Image precedence matches `CartBundleItem.vue`: the customer's own ad-hoc preview wins, then the
    // authored cart listing image, then the first listing-view image.
    cartBundlePreviewImgUrl(): string | null {
      const adHocPreviewImgUrl = this.adHocCartBundlePreviewImageUrl(this.cartBundle.cartBundleId)

      if (adHocPreviewImgUrl) {
        return adHocPreviewImgUrl
      }

      if (this.bundleContent?.cartListingImage) {
        return this.bundleContent.cartListingImage.file.url
      }

      const listingViewImages = this.bundleContent?.listingViewImages

      if (!listingViewImages) {
        return null
      }

      // `ContentfulBundleImagesFlat` has `images`; `FlatContentfulEntry<ContentfulBundleImage>` has `image`.
      if ('images' in listingViewImages && listingViewImages.images.length > 0) {
        return listingViewImages.images[0].image.file.url
      }

      if ('image' in listingViewImages) {
        return listingViewImages.image.file.url
      }

      return null
    },
    // A bundle the customer was granted by a promotion, rather than one they chose. Drives the FREE
    // pill, the "Reward Unlocked!" description in place of the package contents, the single-value
    // price, and the absence of a remove control.
    isReward(): boolean {
      return !!this.cartBundle.sourcePromotionId
    },
    showRemoveButton(): boolean {
      return this.cartBundleIsRemovable(this.cartBundle)
    },
    extendedInfo(): string | null {
      return this.cartBundleExtendedInfo(this.cartBundle.cartBundleId)
    },
    // Shown on reward rows too: a reward bundle can still carry a paid selection, and the row
    // already reads as a reward from the FREE pill rather than from what it omits.
    selections(): CartBundleSelectionItemData[] {
      return this.cartBundleSelections(this.cartBundle.cartBundleId)
    }
  },
  methods: {
    async handleRemovePackageBtnClick(): Promise<void> {
      try {
        // clone the cartBundle in case it's removed with the next action
        const cartBundleToBeDecrementedOrRemoved = cloneDeep(this.cartBundle)

        await this.cartStore.decrementCartBundleQuantityOrRemove(this.cartBundle.cartBundleId)

        if (!this.cartStore.cartBundleIdIsInCart(cartBundleToBeDecrementedOrRemoved.cartBundleId)) {
          // the cartBundle was removed from the cart, so record an event for that

          this.trackGtmEvent('remove_from_cart', {
            ecommerce: {
              currency: 'USD',
              value: cartBundleToBeDecrementedOrRemoved.bundleTotalPostDiscounts,
              items: {
                id: cartBundleToBeDecrementedOrRemoved.bundleIdentifier,
                name: cartBundleToBeDecrementedOrRemoved.bundleNameInternal,
                price: cartBundleToBeDecrementedOrRemoved.bundleTotalPostDiscounts,
                quantity: cartBundleToBeDecrementedOrRemoved.quantity
              } as GoogleAnalyticsCartItem
            }
          })
        }
      } catch (err) {
        this.toast.error(cartChangeErrorMessage(err, feedbackContent.removeBundleFailed))
        throw err
      }
    }
  }
})
</script>
